import { cn } from '@/utils/cn';
import React, { useState } from 'react';

interface PreviewCaptionProps {
  caption: string;
  hashtagColor: string;
  className?: string;
  maxLines?: number; // optional line-clamp
  prefix?: React.ReactNode; // e.g. bold username before caption
  truncated?: boolean; // enable collapse/expand with "see more" / "see less"
}

/**
 * Parse caption text and render with styled hashtags
 */
function renderCaptionWithHashtags(
  caption: string,
  hashtagColor: string
): React.ReactNode {
  // Regex to match hashtags (including Unicode letters)
  const hashtagRegex = /(#[\p{L}\p{N}_]+)/gu;
  const parts = caption.split(hashtagRegex);

  return parts.map((part, index) => {
    if (part.match(hashtagRegex)) {
      return (
        <span key={index} className={cn('font-medium', hashtagColor)}>
          {part}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

export const PreviewCaption = ({
  caption,
  hashtagColor,
  className,
  maxLines,
  prefix,
  truncated = false,
}: PreviewCaptionProps) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = caption.length > 150;
  const shouldTruncate = truncated && isLong && !expanded;

  return (
    <div>
      <p
        className={cn(
          'whitespace-pre-wrap break-words',
          shouldTruncate && 'line-clamp-3',
          !shouldTruncate && maxLines && `line-clamp-${maxLines}`,
          className
        )}
      >
        {prefix && <>{prefix} </>}
        {renderCaptionWithHashtags(caption, hashtagColor)}
      </p>
      {truncated && isLong && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className="text-gray-500 hover:text-gray-700 text-xs mt-0.5 font-medium"
        >
          {expanded ? '...see less' : '...see more'}
        </button>
      )}
    </div>
  );
};
